import React, { useEffect, useMemo, useState } from 'react';
import {
  Table,
  Input,
  Button,
  Space,
  Card,
  Modal,
  message,
  Popconfirm,
  Tag,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  DeleteOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import {
  fetchCollectionDocuments,
  deleteCollectionDocument,
} from '../../services/firestoreService';
import { formatCellValue } from '../../utils/mapFirestoreDoc';

const { Search } = Input;

const renderColumnValue = (value, type) => {
  if (value === null || value === undefined || value === '') {
    return <span className="text-gray-400">—</span>;
  }

  if (type === 'date') {
    const formatted = moment(value).isValid()
      ? moment(value).format('MMM DD, YYYY HH:mm')
      : formatCellValue(value);
    return <span>{formatted}</span>;
  }

  if (type === 'boolean') {
    return <Tag color={value ? 'green' : 'default'}>{value ? 'Yes' : 'No'}</Tag>;
  }

  if (typeof value === 'object') {
    return <span className="text-xs font-mono">{JSON.stringify(value)}</span>;
  }

  return <span>{String(value)}</span>;
};

const FirebaseCollectionPage = ({ config }) => {
  const { logActivity } = useAdminAuth();
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);

  const loadRecords = async () => {
    setLoading(true);
    try {
      const data = await fetchCollectionDocuments(config.collection);
      setRecords(data);
    } catch {
      message.error(`Failed to load ${config.title} from Firebase`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecords();
    logActivity(`view_${config.key}`, `Viewed ${config.title} page`);
  }, [config.key, config.collection, config.title]);

  useEffect(() => {
    if (!searchText) {
      setFilteredRecords(records);
      return;
    }

    const query = searchText.toLowerCase();
    const filtered = records.filter((record) =>
      config.searchFields.some((field) =>
        String(record[field] ?? '').toLowerCase().includes(query)
      ) ||
      record.id.toLowerCase().includes(query)
    );
    setFilteredRecords(filtered);
  }, [records, searchText, config.searchFields]);

  const handleDelete = async (record) => {
    try {
      await deleteCollectionDocument(config.collection, record.id);
      message.success('Record deleted successfully');
      logActivity(`delete_${config.key}`, `Deleted ${config.title} record: ${record.id}`);
      await loadRecords();
    } catch {
      message.error('Failed to delete record');
    }
  };

  const tableColumns = useMemo(() => {
    const dataColumns = config.columns.map((col) => ({
      title: col.title,
      dataIndex: col.dataIndex,
      key: col.key,
      ellipsis: col.ellipsis,
      render: (value, record) => {
        const resolved =
          value ??
          record[col.dataIndex] ??
          (col.dataIndex === 'photoCount' && Array.isArray(record.photos)
            ? record.photos.length
            : undefined);
        return renderColumnValue(resolved, col.type);
      },
    }));

    return [
      ...dataColumns,
      {
        title: 'Actions',
        key: 'actions',
        width: 120,
        render: (_, record) => (
          <Space size="small">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedRecord(record);
                setIsViewModalVisible(true);
              }}
            />
            <Popconfirm
              title="Delete this record?"
              onConfirm={() => handleDelete(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ),
      },
    ];
  }, [config]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{config.title}</h1>
          <p className="text-gray-500 mt-1">{config.description}</p>
          <p className="text-xs text-gray-400 mt-1 font-mono">Collection: {config.collection}</p>
        </div>
        <Button icon={<ReloadOutlined />} onClick={loadRecords} loading={loading}>
          Refresh
        </Button>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Card>
            <Statistic
              title="Total Records"
              value={records.length}
              prefix={<DatabaseOutlined className="text-cyan-500" />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card>
            <Statistic
              title="Showing"
              value={filteredRecords.length}
              prefix={<SearchOutlined className="text-blue-500" />}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Search
          placeholder={`Search ${config.title.toLowerCase()}...`}
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
          style={{ maxWidth: 400, marginBottom: 16 }}
        />

        <Table
          columns={tableColumns}
          dataSource={filteredRecords}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} records`,
          }}
          scroll={{ x: true }}
          locale={{ emptyText: `No ${config.title.toLowerCase()} found in Firebase` }}
        />
      </Card>

      <Modal
        title={`${config.title} Details`}
        open={isViewModalVisible}
        onCancel={() => setIsViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={700}
      >
        {selectedRecord && (
          <div className="space-y-3">
            <div>
              <p className="text-gray-500 text-sm">Document ID</p>
              <p className="font-mono text-sm break-all">{selectedRecord.id}</p>
            </div>
            <pre className="bg-gray-50 rounded-lg p-4 text-xs overflow-auto max-h-96">
              {JSON.stringify(selectedRecord, null, 2)}
            </pre>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default FirebaseCollectionPage;
